import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('nba_token');
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.authenticated) {
        setUser(response.data.user);
        setSubscription(response.data.subscription);
      } else {
        localStorage.removeItem('nba_token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('nba_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    
    try {
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        localStorage.setItem('nba_token', response.data.token);
        setUser(response.data.user);
        setSubscription({
          active: response.data.user.is_active,
          type: response.data.user.is_admin ? 'admin' : (response.data.user.is_premium ? 'premium' : 'trial'),
          days_left: response.data.user.days_left
        });
        return { success: true, isAdmin: response.data.user.is_admin };
      } else {
        setError(response.data.error);
        return { success: false, error: response.data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Giriş başarısız';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (email, phone, password) => {
    setError(null);
    
    try {
      const response = await axios.post(`${API_BASE}/auth/register`, {
        email,
        phone,
        password
      });

      if (response.data.success) {
        localStorage.setItem('nba_token', response.data.token);
        setUser(response.data.user);
        setSubscription({
          active: true,
          type: 'trial',
          days_left: 7
        });
        return { success: true };
      } else {
        setError(response.data.error);
        return { success: false, error: response.data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Kayıt başarısız';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    localStorage.removeItem('nba_token');
    setUser(null);
    setSubscription(null);
  };

  const value = {
    user,
    subscription,
    loading,
    error,
    isAuthenticated: !!user,
    isActive: subscription?.active || false,
    login,
    register,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;


